import React, { useState } from 'react'
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap'
import { PnsMacMiniStatus, PnsMacMini } from 'interfaces/pns-mac-mini'
import { PhoneStatus, PhysicalPhone } from 'interfaces/physical-phone'
import useUpdateMacMiniStatus from 'hooks/use-update-mac-mini-status'
import useUpdatePhysicalPhoneStatus from 'hooks/use-update-physical-phone-status'
import { MacMiniUserCreationModal } from 'components/mac-mini-assignment'
import {
  PhysicalPhoneMacMiniUserUpdateModal,
  PhysicalPhoneToActiveMacMiniUserModal,
} from 'components/physical-phone-mac-mini-user'

type DeviceStatus = PnsMacMiniStatus | PhoneStatus
type DeviceType = 'mac-mini' | 'physical-phone'

interface DeviceStatusUpdateModalProps {
  show: boolean
  onHide: () => void
  deviceId: string
  deviceName: string
  deviceType: DeviceType
  currentStatus: DeviceStatus
  onSuccess?: () => void
  macMini?: PnsMacMini // Required for Mac Mini user creation flow
  physicalPhone?: PhysicalPhone // Required for Physical Phone Mac Mini user update flow
}

const DeviceStatusUpdateModal: React.FC<DeviceStatusUpdateModalProps> = ({
  show,
  onHide,
  deviceId,
  deviceName,
  deviceType,
  currentStatus,
  onSuccess,
  macMini,
  physicalPhone,
}) => {
  const [selectedStatus, setSelectedStatus] =
    useState<DeviceStatus>(currentStatus)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showUserCreationModal, setShowUserCreationModal] = useState(false)
  const [showPhoneMacMiniUserModal, setShowPhoneMacMiniUserModal] =
    useState(false)
  const [showPhoneToActiveModal, setShowPhoneToActiveModal] = useState(false)

  const { mutate: updateMacMiniStatus, isLoading: macMiniLoading } =
    useUpdateMacMiniStatus()
  const { mutate: updatePhoneStatus, isLoading: phoneLoading } =
    useUpdatePhysicalPhoneStatus()

  const isLoading = macMiniLoading || phoneLoading

  // Available status options (excluding RECEIVED)
  const macMiniStatusOptions = Object.values(PnsMacMiniStatus).filter(
    (status) => status !== PnsMacMiniStatus.RECEIVED
  )
  const phoneStatusOptions = Object.values(PhoneStatus).filter(
    (status) => status !== PhoneStatus.RECEIVED
  )

  const availableStatuses =
    deviceType === 'mac-mini' ? macMiniStatusOptions : phoneStatusOptions

  const getStatusVariant = (status: DeviceStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success'
      case 'AVAILABLE':
        return 'info'
      case 'PROVISIONING':
      case 'ICLOUD_SETUP_IN_PROGRESS':
      case 'WARMING':
        return 'info'
      case 'MAINTENANCE':
      case 'RELOCATING':
        return 'warning'
      case 'OFFLINE':
        return 'secondary'
      case 'DECOMMISSIONED':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getStatusLabel = (status: DeviceStatus) => {
    return status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (selectedStatus === currentStatus) {
      onHide()
      return
    }

    setShowConfirmation(true)
  }

  const handleConfirmUpdate = () => {
    // Check if this is Mac Mini transitioning from PROVISIONING to AVAILABLE
    if (
      deviceType === 'mac-mini' &&
      currentStatus === PnsMacMiniStatus.PROVISIONING &&
      selectedStatus === PnsMacMiniStatus.AVAILABLE &&
      macMini
    ) {
      // Show user creation modal instead of directly updating status
      setShowUserCreationModal(true)
      return
    }

    // Check if this is Physical Phone transitioning from AVAILABLE to ICLOUD_SETUP_IN_PROGRESS
    if (
      deviceType === 'physical-phone' &&
      currentStatus === PhoneStatus.AVAILABLE &&
      selectedStatus === PhoneStatus.ICLOUD_SETUP_IN_PROGRESS &&
      physicalPhone
    ) {
      // Show Mac Mini user update modal instead of directly updating status
      setShowPhoneMacMiniUserModal(true)
      return
    }

    // Check if this is Physical Phone transitioning to ACTIVE from WARMING or ICLOUD_SETUP_IN_PROGRESS
    if (
      deviceType === 'physical-phone' &&
      (currentStatus === PhoneStatus.WARMING ||
        currentStatus === PhoneStatus.ICLOUD_SETUP_IN_PROGRESS) &&
      selectedStatus === PhoneStatus.ACTIVE &&
      physicalPhone
    ) {
      // Show Phone to Active Mac Mini user modal instead of directly updating status
      setShowPhoneToActiveModal(true)
      return
    }

    // Normal status update flow
    if (deviceType === 'mac-mini') {
      updateMacMiniStatus(
        {
          macMiniId: deviceId,
          status: selectedStatus as PnsMacMiniStatus,
        },
        {
          onSuccess: () => {
            onSuccess?.()
            onHide()
          },
        }
      )
    } else {
      updatePhoneStatus(
        {
          phoneId: deviceId,
          status: selectedStatus as PhoneStatus,
        },
        {
          onSuccess: () => {
            onSuccess?.()
            onHide()
          },
        }
      )
    }
  }

  const handleUserCreationSuccess = () => {
    // After users are created, update the Mac Mini status to AVAILABLE
    updateMacMiniStatus(
      {
        macMiniId: deviceId,
        status: PnsMacMiniStatus.AVAILABLE,
      },
      {
        onSuccess: () => {
          setShowUserCreationModal(false)
          onSuccess?.()
          onHide()
        },
      }
    )
  }

  const handleUserCreationCancel = () => {
    setShowUserCreationModal(false)
  }

  const handlePhoneMacMiniUserUpdateSuccess = () => {
    // After Mac Mini user is updated, update the Physical Phone status to ICLOUD_SETUP_IN_PROGRESS
    updatePhoneStatus(
      {
        phoneId: deviceId,
        status: PhoneStatus.ICLOUD_SETUP_IN_PROGRESS,
      },
      {
        onSuccess: () => {
          setShowPhoneMacMiniUserModal(false)
          onSuccess?.()
          onHide()
        },
      }
    )
  }

  const handlePhoneMacMiniUserUpdateCancel = () => {
    setShowPhoneMacMiniUserModal(false)
  }

  const handlePhoneToActiveSuccess = () => {
    // After Mac Mini user is updated, update the Physical Phone status to ACTIVE
    updatePhoneStatus(
      {
        phoneId: deviceId,
        status: PhoneStatus.ACTIVE,
      },
      {
        onSuccess: () => {
          setShowPhoneToActiveModal(false)
          onSuccess?.()
          onHide()
        },
      }
    )
  }

  const handlePhoneToActiveCancel = () => {
    setShowPhoneToActiveModal(false)
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    setSelectedStatus(currentStatus)
  }

  const resetModal = () => {
    setShowConfirmation(false)
    setShowUserCreationModal(false)
    setShowPhoneMacMiniUserModal(false)
    setShowPhoneToActiveModal(false)
    setSelectedStatus(currentStatus)
  }

  React.useEffect(() => {
    if (!show) {
      resetModal()
    }
  }, [show, currentStatus])

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {showConfirmation
              ? 'Confirm Status Change'
              : `Update ${
                  deviceType === 'mac-mini' ? 'Mac Mini' : 'Physical Phone'
                } Status`}
          </Modal.Title>
        </Modal.Header>

        {showConfirmation ? (
          // Confirmation View
          <>
            <Modal.Body>
              <Alert variant="warning">
                <Alert.Heading>⚠️ Confirm Status Change</Alert.Heading>
                <p>
                  You are about to change the status of{' '}
                  <strong>{deviceName}</strong>.
                </p>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Current Status:</strong>
                    <Badge
                      variant={getStatusVariant(currentStatus)}
                      className="ms-2"
                    >
                      {getStatusLabel(currentStatus)}
                    </Badge>
                  </div>
                  <div>
                    <strong>New Status:</strong>
                    <Badge
                      variant={getStatusVariant(selectedStatus)}
                      className="ms-2"
                    >
                      {getStatusLabel(selectedStatus)}
                    </Badge>
                  </div>
                </div>
              </Alert>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="warning"
                onClick={handleConfirmUpdate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Confirm Change'
                )}
              </Button>
            </Modal.Footer>
          </>
        ) : (
          // Status Selection View
          <>
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <div className="mb-3">
                  <strong>Device:</strong> {deviceName}
                </div>

                <div className="mb-3">
                  <strong>Current Status:</strong>
                  <Badge
                    variant={getStatusVariant(currentStatus)}
                    className="ms-2"
                  >
                    {getStatusLabel(currentStatus)}
                  </Badge>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Select New Status:</strong>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedStatus}
                    onChange={(e) =>
                      setSelectedStatus(e.target.value as DeviceStatus)
                    }
                  >
                    {availableStatuses.map((status) => (
                      <option key={status} value={status}>
                        {getStatusLabel(status)}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>

                <Alert variant="info">
                  <strong>Note:</strong> Changing the device status may affect
                  its availability and assignment within the system.
                </Alert>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={selectedStatus === currentStatus}
                >
                  Review Change
                </Button>
              </Modal.Footer>
            </Form>
          </>
        )}
      </Modal>

      {/* Mac Mini User Creation Modal */}
      {macMini && (
        <MacMiniUserCreationModal
          show={showUserCreationModal}
          onHide={handleUserCreationCancel}
          macMini={macMini}
          onSuccess={handleUserCreationSuccess}
          onCancel={handleUserCreationCancel}
        />
      )}

      {/* Physical Phone Mac Mini User Update Modal */}
      {physicalPhone && (
        <PhysicalPhoneMacMiniUserUpdateModal
          show={showPhoneMacMiniUserModal}
          onHide={handlePhoneMacMiniUserUpdateCancel}
          physicalPhone={physicalPhone}
          onSuccess={handlePhoneMacMiniUserUpdateSuccess}
          onCancel={handlePhoneMacMiniUserUpdateCancel}
        />
      )}

      {/* Physical Phone To Active Mac Mini User Modal */}
      {physicalPhone && (
        <PhysicalPhoneToActiveMacMiniUserModal
          show={showPhoneToActiveModal}
          onHide={handlePhoneToActiveCancel}
          physicalPhone={physicalPhone}
          onSuccess={handlePhoneToActiveSuccess}
          onCancel={handlePhoneToActiveCancel}
        />
      )}
    </>
  )
}

export default DeviceStatusUpdateModal
